import React, { useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Box,
  VStack,
  Text,
  Flex,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import useRequest from 'src/utils/hooks/useRequest';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { useRouter } from 'next/router';
import { LOMA_BRAND_COLORS } from 'src/constants/ui';

interface LaunchPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  launchPreviewRequested?: boolean;
}

const LaunchPreviewModal = ({
  isOpen,
  onClose,
  launchPreviewRequested = false,
}: LaunchPreviewModalProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { api } = useRequest();
  const { marketplace } = useContext(MarketplaceContext);
  const router = useRouter();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const onConfirmLaunch = async () => {
    if (!marketplace) return;
    setIsLoading(true);
    try {
      await api.marketplace.launchPreview(marketplace.id);
      const hostname = window.location.hostname || marketplace?.domain?.domain;
      localStorage.removeItem(`marketplace-${hostname}`);
      onClose();
      router.reload();
    } catch (error) {
      let errorMessage = 'Could not launch preview.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} size="md">
      <AlertDialogOverlay bg="blackAlpha.600" backdropFilter="blur(10px)">
        <AlertDialogContent
          borderRadius="2xl"
          bg="white"
          boxShadow="2xl"
          border="1px solid"
          borderColor="gray.100"
          overflow="hidden"
        >
          {/* Header with gradient background */}
          <Box
            bgGradient={`linear(90deg, ${LOMA_BRAND_COLORS.lightGreen}, ${LOMA_BRAND_COLORS.darkestLightGreen})`}
            px={6}
            py={8}
            textAlign="center"
          >
            <VStack spacing={3}>
              <Box bg="whiteAlpha.200" borderRadius="full" p={4} backdropFilter="blur(10px)">
                <Icon as={ArrowUpIcon} w={8} h={8} color="white" />
              </Box>
              <AlertDialogHeader
                fontSize="2xl"
                fontWeight="bold"
                color="white"
                p={0}
                textAlign="center"
              >
                {launchPreviewRequested ? 'Already on Launch Waitlist' : 'Ready to Go Live?'}
              </AlertDialogHeader>
            </VStack>
          </Box>

          <AlertDialogBody px={8} py={6}>
            <VStack spacing={4} textAlign="center">
              <Text fontSize="md" color="gray.600" lineHeight="1.6">
                {launchPreviewRequested
                  ? "You're already on the launch waitlist! We'll review your site and let you know when it goes live."
                  : "We'll add you to the launch queue and review your site before taking it live. Once live, the site will be accessible to everyone."}
              </Text>

              <Box
                bg="blue.50"
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor="blue.100"
                w="full"
              >
                <Flex align="center" justify="center" gap={2}>
                  <Text fontSize="sm" color="blue.700" fontWeight="medium">
                    📧 Look out for an email from Loma letting you know when your site is live!
                  </Text>
                </Flex>
              </Box>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter px={8} pb={6} pt={2}>
            <Flex gap={3} w="full">
              <Button
                ref={cancelRef}
                onClick={onClose}
                variant="outline"
                colorScheme="gray"
                flex={1}
                borderRadius="lg"
                h={12}
              >
                {launchPreviewRequested ? 'Close' : 'Not Yet'}
              </Button>
              {!launchPreviewRequested && (
                <Button
                  colorScheme="blue"
                  onClick={onConfirmLaunch}
                  flex={1}
                  bgGradient={`linear(135deg, ${LOMA_BRAND_COLORS.lightGreen}, ${LOMA_BRAND_COLORS.darkestLightGreen})`}
                  _hover={{
                    bgGradient: `linear(135deg, ${LOMA_BRAND_COLORS.darkerLightGreen}, ${LOMA_BRAND_COLORS.darkestLightGreen})`,
                    transform: 'translateY(-1px)',
                    boxShadow: 'lg',
                  }}
                  _active={{
                    transform: 'translateY(0)',
                  }}
                  borderRadius="lg"
                  h={12}
                  fontWeight="semibold"
                  isLoading={isLoading}
                  loadingText="Joining Queue..."
                >
                  Join Launch Queue
                </Button>
              )}
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default LaunchPreviewModal;
